import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Calendar, Heart, Users, Check, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { PrimaryButton, IconButton } from '../components/brand/BrandButton';
import { BrandCard, StatsCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { Badge } from '../components/brand/BrandIndicators';
import { Container } from '../components/brand/BrandLayout';
import { CoRegulateMark } from '../components/Logo';

const History = () => {
    const navigate = useNavigate();
    const { api } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [sessionsRes, statsRes] = await Promise.all([
                    api().get('/regulation/sessions?limit=50'),
                    api().get('/stats/summary')
                ]);
                setSessions(sessionsRes.data);
                setStats(statsRes.data);
            } catch (error) {
                toast.error('Unable to load history');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [api]);

    const formatOverwhelmType = (type) => {
        return type?.charAt(0).toUpperCase() + type?.slice(1) || 'Unknown';
    };

    const groupSessionsByDate = (sessions) => {
        const groups = {};
        sessions.forEach(session => {
            const date = new Date(session.created_at).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
            });
            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(session);
        });
        return groups;
    };

    const groupedSessions = groupSessionsByDate(sessions);

    if (loading) {
        return (
            <div className="min-h-screen bg-transparent flex items-center justify-center">
                <div className="w-4 h-4 rounded-full bg-[#7D8E95] brand-breathe" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-transparent">
            <header className="sticky top-0 z-50 glass-header">
                <div className="max-w-md mx-auto px-4 sm:px-6 py-3 sm:py-5 flex items-center justify-between">
                    <IconButton 
                        onClick={() => navigate('/home')}
                        data-testid="back-btn"
                        className="text-[#8B7B6B] hover:text-[#6B5D4D] hover:bg-[#FFBB98]/40 transition-all duration-200"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                    <Heading as="h1" size="text-base sm:text-lg">Your History</Heading>
                    <button
                        onClick={() => navigate('/home')}
                        className="hover:opacity-80 transition-opacity"
                        data-testid="header-logo"
                        aria-label="Go to dashboard"
                    >
                        <CoRegulateMark size={28} />
                    </button>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
                {/* Stats Summary */}
                {stats && stats.total_sessions > 0 && (
                    <section className="mb-10 brand-fade-in">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="glass-card rounded-2xl p-4 text-center">
                                <div className="text-2xl font-semibold text-[#1D6B6B]">{stats.total_sessions}</div>
                                <SmallText className="text-[#7D8E95]">Total</SmallText>
                            </div>
                            <div className="glass-card-sage rounded-2xl p-4 text-center">
                                <div className="text-2xl font-semibold text-[#1D6B6B]">{stats.completed_sessions}</div>
                                <SmallText className="text-[#7D8E95]">Completed</SmallText>
                            </div>
                            <div className="glass-card-lavender rounded-2xl p-4 text-center">
                                <div className="text-2xl font-semibold text-[#1D6B6B]">{stats.completion_rate}%</div>
                                <SmallText className="text-[#7D8E95]">Rate</SmallText>
                            </div>
                        </div>
                    </section>
                )}

                {/* Sessions List */}
                {sessions.length > 0 ? (
                    <section>
                        {Object.entries(groupedSessions).map(([date, daySessions], groupIndex) => (
                            <div key={date} className="mb-10">
                                <div className="flex items-center gap-2 mb-5">
                                    <Calendar className="w-4 h-4 text-[#7D8E95]" />
                                    <SmallText className="font-medium">
                                        {date}
                                    </SmallText>
                                </div>
                                <div className="space-y-3">
                                    {daySessions.map((session, index) => (
                                        <div 
                                            key={session.id}
                                            className={`glass-card rounded-2xl p-3 sm:p-4 card-hover brand-slide-up ${session.relational_context === 'partnered' ? 'glass-card-rose' : 'glass-card-sage'}`}
                                            style={{ animationDelay: `${(groupIndex * daySessions.length + index) * 30}ms` }}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex items-start gap-2 sm:gap-4 min-w-0 flex-1">
                                                    <IconContainer 
                                                        variant={session.relational_context === 'partnered' ? 'rose' : 'sage'} 
                                                        size="lg"
                                                        useGradient={true}
                                                        className="flex-shrink-0"
                                                    >
                                                        {session.relational_context === 'partnered' ? (
                                                            <Users className="w-5 h-5" />
                                                        ) : (
                                                            <Heart className="w-5 h-5" />
                                                        )}
                                                    </IconContainer>
                                                    <div className="min-w-0">
                                                        <BodyText className="font-medium text-[#1D6B6B] truncate">
                                                            {formatOverwhelmType(session.overwhelm_type)}
                                                        </BodyText>
                                                        <SmallText className="text-xs sm:text-sm">
                                                            {session.relational_context === 'partnered' ? 'Co-regulation' : 'Individual'} &bull; {session.actions_completed} steps
                                                        </SmallText>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                    <SmallText className="text-xs sm:text-sm whitespace-nowrap">
                                                        {new Date(session.created_at).toLocaleTimeString('en-US', {
                                                            hour: 'numeric',
                                                            minute: '2-digit'
                                                        })}
                                                    </SmallText>
                                                    {session.completed_at ? (
                                                        <Badge variant="sage" size="sm" className="whitespace-nowrap">
                                                            <Check className="w-3 h-3" />
                                                            Done
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="neutral" size="sm" className="whitespace-nowrap">
                                                            <Clock className="w-3 h-3" />
                                                            <span className="hidden sm:inline">In progress</span>
                                                            <span className="sm:hidden">Active</span>
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </section>
                ) : (
                    <section className="text-center py-16 brand-fade-in">
                        <div className="glass-card-sage w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center">
                            <Calendar className="w-10 h-10 text-[#1D6B6B]" />
                        </div>
                        <Heading as="h3" size="text-xl" className="mb-3">
                            No sessions yet
                        </Heading>
                        <BodyText className="mb-8 max-w-sm mx-auto">
                            Your regulation history will appear here after your first session.
                        </BodyText>
                        <PrimaryButton 
                            onClick={() => navigate('/regulate')}
                            size="xl"
                            data-testid="start-session-btn"
                        >
                            Start Regulating
                        </PrimaryButton>
                    </section>
                )}
            </main>
        </div>
    );
};

export default History;
